import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield, Lock, Database, Eye, UserCheck, Globe, Mail, FileText,
  ArrowLeft, Calendar, DollarSign, RefreshCw, Clock, CheckCircle,
  XCircle, CreditCard, Gift, Scale, AlertTriangle, Ban, Gavel,
  Cookie, Settings, BarChart3, Target, ToggleLeft, Info,
  FileCheck, Users, Sparkles, Loader2
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "http://localhost:8001/api";

const ICON_MAP = {
  Shield, Lock, Database, Eye, UserCheck, Globe, Mail, FileText,
  Calendar, DollarSign, RefreshCw, Clock, CheckCircle, XCircle,
  CreditCard, Gift, Scale, AlertTriangle, Ban, Gavel, Cookie,
  Settings, BarChart3, Target, ToggleLeft, Info, FileCheck,
  Users, Sparkles
};

const GRADIENT_MAP = {
  "privacy-policy": "from-blue-600 to-purple-600",
  "terms-of-service": "from-purple-600 to-pink-600",
  "refund-policy": "from-green-600 to-teal-600",
  "cookie-policy": "from-orange-500 to-amber-600",
  "acceptable-use": "from-red-600 to-rose-600"
};

const BG_MAP = {
  "privacy-policy": "from-slate-50 to-blue-50",
  "terms-of-service": "from-slate-50 to-purple-50",
  "refund-policy": "from-slate-50 to-green-50",
  "cookie-policy": "from-slate-50 to-orange-50",
  "acceptable-use": "from-slate-50 to-red-50"
};

const ICON_COLOR_MAP = {
  "privacy-policy": "text-blue-600",
  "terms-of-service": "text-purple-600",
  "refund-policy": "text-green-600",
  "cookie-policy": "text-orange-600",
  "acceptable-use": "text-red-600"
};

const CROSS_LINKS = {
  "privacy-policy": [
    { label: "Terms of Service", path: "/terms" },
    { label: "Cookie Policy", path: "/cookies" },
    { label: "Refund Policy", path: "/refund" }
  ],
  "terms-of-service": [
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Acceptable Use Policy", path: "/acceptable-use" },
    { label: "Refund Policy", path: "/refund" }
  ],
  "refund-policy": [
    { label: "Terms of Service", path: "/terms" },
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Back to Home", path: "/" }
  ],
  "cookie-policy": [
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Terms of Service", path: "/terms" },
    { label: "Back to Home", path: "/" }
  ],
  "acceptable-use": [
    { label: "Terms of Service", path: "/terms" },
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Back to Home", path: "/" }
  ]
};

const renderContent = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={i} />;

    // Bold headers like **text**
    if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
      return <h3 key={i} className="text-xl font-semibold text-gray-800 mt-6">{trimmed.replace(/\*\*/g, "")}</h3>;
    }

    // Bold prefix like **Label:** rest
    const boldMatch = trimmed.match(/^\*\*(.+?)\*\*\s*(.*)/);
    if (boldMatch && trimmed.startsWith("**")) {
      // Check if it's a bullet
      return (
        <p key={i} className="text-gray-600 leading-relaxed">
          <strong>{boldMatch[1]}</strong> {boldMatch[2]}
        </p>
      );
    }

    // Bullet points
    if (trimmed.startsWith("- ")) {
      const content = trimmed.substring(2);
      const boldBullet = content.match(/^\*\*(.+?)\*\*:?\s*(.*)/);
      if (boldBullet) {
        return (
          <li key={i} className="text-gray-600">
            <strong>{boldBullet[1]}:</strong> {boldBullet[2]}
          </li>
        );
      }
      return <li key={i} className="text-gray-600">{content}</li>;
    }

    // Numbered items
    const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
    if (numMatch) {
      const content = numMatch[2];
      const boldNum = content.match(/^\*\*(.+?)\*\*:?\s*(.*)/);
      if (boldNum) {
        return (
          <li key={i} className="text-gray-600" style={{ listStyleType: "decimal" }}>
            <strong>{boldNum[1]}:</strong> {boldNum[2]}
          </li>
        );
      }
      return <li key={i} className="text-gray-600" style={{ listStyleType: "decimal" }}>{content}</li>;
    }

    return <p key={i} className="text-gray-600 leading-relaxed">{trimmed}</p>;
  });
};

const LegalPageRenderer = ({ slug, testId }) => {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/legal-pages/${slug}`);
        setPageData(res.data);
      } catch {
        // Will use null → shows loading failed
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-gray-500">Failed to load page content.</p>
      </div>
    );
  }

  const gradient = GRADIENT_MAP[slug] || "from-gray-600 to-gray-800";
  const bgGradient = BG_MAP[slug] || "from-slate-50 to-gray-50";
  const iconColor = ICON_COLOR_MAP[slug] || "text-blue-600";
  const HeaderIcon = ICON_MAP[pageData.icon] || Shield;
  const crossLinks = CROSS_LINKS[slug] || [];

  return (
    <div className={`min-h-screen bg-gradient-to-br ${bgGradient}`} data-testid={testId}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradient} text-white py-16`}>
        <div className="container mx-auto px-4 max-w-4xl">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="legal-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <HeaderIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">{pageData.title}</h1>
              <p className="text-white/80 mt-1">{pageData.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Last updated: {pageData.last_updated}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-4xl py-12">
        <Card className="shadow-xl border-0">
          <CardContent className="p-8 prose prose-slate max-w-none">
            {pageData.sections?.map((section, index) => {
              const SectionIcon = ICON_MAP[section.icon] || FileText;
              const content = renderContent(section.content);
              const hasBullets = section.content?.includes("\n- ");
              const hasNumbers = section.content?.match(/\n\d+\.\s/);

              return (
                <section key={section.id || index} className="mb-8" data-testid={`section-${section.id || index}`}>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <SectionIcon className={`w-6 h-6 ${iconColor}`} />
                    {section.title}
                  </h2>
                  <div className="mt-3">
                    {hasBullets || hasNumbers ? (
                      <ul className="list-disc pl-6 space-y-2">
                        {content}
                      </ul>
                    ) : (
                      content
                    )}
                  </div>
                </section>
              );
            })}
          </CardContent>
        </Card>

        {/* Cross Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          {crossLinks.map((link, i) => (
            <Button key={i} variant="outline" onClick={() => navigate(link.path)}>
              {link.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LegalPageRenderer;
